import { useToast } from "@/hooks/use-toast";
import { useActiveEntity } from "@/lib/entity-context";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Resolve the entity name a switch confirmation should announce.
 *
 * Returns `null` when the id names nothing we can confirm - an unknown or
 * removed entity. Callers must stay silent in that case rather than announce
 * a switch they cannot describe.
 *
 * Exported for unit testing; `useEntitySwitch` is the production consumer.
 */
export function resolveSwitchedEntityName(
  id: string,
  entities: readonly { id: string; name: string; }[],
  allEntitiesLabel: string,
): string | null {
  if (id === "all") return allEntitiesLabel;
  return entities.find((entity) => entity.id === id)?.name ?? null;
}

/**
 * User-initiated entity switch.
 *
 * OCT-10356: switching entities gave no unmistakable feedback - the page
 * stays put and only the switcher's own label changes, which is easy to miss.
 * This hook is the single place a user-driven switch is confirmed, so every
 * switcher surface (header dropdown, All Entities banner) announces the same
 * thing.
 *
 * The confirmation lives here rather than inside `setActiveEntityId` because
 * `EntityProvider`'s auto-landing effect also calls that setter on boot and
 * when a persisted selection has to be corrected. A toast there would fire on
 * page load, not on a user action.
 *
 * OCT-4558 is preserved: this does not navigate. `setActiveEntityId` already
 * invalidates entity-scoped query keys, so the current page refetches in
 * place and report paths, filters, and other page context survive the switch.
 */
export function useEntitySwitch(): (id: string) => void {
  const { entities, activeEntityId, setActiveEntityId } = useActiveEntity();
  const { toast } = useToast();
  const { t } = useTranslation("common");

  return useCallback((id: string) => {
    if (id === activeEntityId) return;
    const name = resolveSwitchedEntityName(id, entities, t("nav.allEntities"));
    setActiveEntityId(id);
    if (name === null) return;
    toast({
      title: t("nav.entitySwitched", { name }),
      duration: 4000,
      "data-testid": "toast-entity-switched",
    });
  }, [activeEntityId, entities, setActiveEntityId, t, toast]);
}
